/**
 * Admin API facade.
 *
 * Each function talks to `seen-backend` when `VITE_API_BASE_URL` is set and
 * otherwise falls back to the session-scoped mock implementations in
 * `admin.mock.ts`, so call sites, signatures and query keys never change.
 */


import * as mock from "./admin.mock";
import { api, trackVisitRemote, uploadFile, USE_MOCK_API } from "./http";
import type { CategoryTab } from "./types";

export type {
  AdminCategory,
  AdminColor,
  AdminMetrics,
  AdminOrder,
  AdminOrderItem,
  AdminProduct,
  AdminRole,
  AdminUser,
  AnalyticsRange,
  AnalyticsSummary,
  AppearanceSettings,
  CategorySales,
  GeneralSettings,
  OrderStatus,
  PathHit,
  ProductColorImage,
  ProductColorVariant,
  RangePreset,
  SalesPoint,
  SalesTimePoint,
  ShippingArea,
  ShippingSettings,
  StatusSlice,
  TopProduct,
  VisitorPoint,
} from "./admin.mock";

import type {
  AdminCategory,
  AdminColor,
  AdminMetrics,
  AdminOrder,
  AdminProduct,
  AdminRole,
  AdminUser,
  AnalyticsRange,
  AnalyticsSummary,
  AppearanceSettings,
  CategorySales,
  GeneralSettings,
  OrderStatus,
  ProductColorImage,
  ProductColorVariant,
  SalesPoint,
  ShippingArea,
  ShippingSettings,
} from "./admin.mock";

export const ORDER_STATUSES = mock.ORDER_STATUSES;
export const VALID_SALES_STATUSES = mock.VALID_SALES_STATUSES;
export const presetRange = mock.presetRange;
export const productSizes = mock.productSizes;
export const categoryOptions = mock.categoryOptions;

/** Records a storefront page view (POST /api/catalog/visits when live). */
export function trackVisit(path: string, sessionId?: string): void {
  if (USE_MOCK_API) {
    mock.trackVisit(path, sessionId);
    return;
  }
  trackVisitRemote(path);
}

/* -------------------------------------------------------------------------- */
/*  Dashboard + analytics                                                     */
/* -------------------------------------------------------------------------- */

export function getMetrics(): Promise<AdminMetrics> {
  return USE_MOCK_API ? mock.getMetrics() : api.get<AdminMetrics>("/admin/metrics");
}

export function getSalesSeries(): Promise<SalesPoint[]> {
  return USE_MOCK_API ? mock.getSalesSeries() : api.get<SalesPoint[]>("/admin/sales-series");
}

export function getCategorySales(): Promise<CategorySales[]> {
  return USE_MOCK_API
    ? mock.getCategorySales()
    : api.get<CategorySales[]>("/admin/category-sales");
}

export function getAnalytics(range: AnalyticsRange): Promise<AnalyticsSummary> {
  return USE_MOCK_API
    ? mock.getAnalytics(range)
    : api.get<AnalyticsSummary>("/admin/analytics", { from: range.from, to: range.to });
}

export function clearVisits(): Promise<void> {
  return USE_MOCK_API ? mock.clearVisits() : api.del<void>("/admin/analytics/visits");
}

/* -------------------------------------------------------------------------- */
/*  Orders + users                                                            */
/* -------------------------------------------------------------------------- */

export function getOrders(): Promise<AdminOrder[]> {
  return USE_MOCK_API ? mock.getOrders() : api.get<AdminOrder[]>("/admin/orders");
}

export function updateOrderStatus(id: string, status: OrderStatus): Promise<AdminOrder> {
  return USE_MOCK_API
    ? mock.updateOrderStatus(id, status)
    : api.patch<AdminOrder>(`/admin/orders/${encodeURIComponent(id)}/status`, { status });
}

export function updateOrder(input: {
  id: string;
  status: OrderStatus;
  discount: number;
}): Promise<AdminOrder> {
  return USE_MOCK_API
    ? mock.updateOrder(input)
    : api.patch<AdminOrder>(`/admin/orders/${encodeURIComponent(input.id)}`, {
        status: input.status,
        discount: input.discount,
      });
}

export function getUsers(): Promise<AdminUser[]> {
  return USE_MOCK_API ? mock.getUsers() : api.get<AdminUser[]>("/admin/users");
}

export function updateUserRole(id: string, role: AdminRole): Promise<AdminUser> {
  return USE_MOCK_API
    ? mock.updateUserRole(id, role)
    : api.patch<AdminUser>(`/admin/users/${encodeURIComponent(id)}/role`, { role });
}

/* -------------------------------------------------------------------------- */
/*  Catalogue                                                                 */
/* -------------------------------------------------------------------------- */

export function getAdminProducts(): Promise<AdminProduct[]> {
  return USE_MOCK_API ? mock.getAdminProducts() : api.get<AdminProduct[]>("/admin/products");
}

export function saveAdminProduct(input: AdminProduct): Promise<AdminProduct> {
  if (USE_MOCK_API) return mock.saveAdminProduct(input);
  return input.id
    ? api.patch<AdminProduct>(`/admin/products/${encodeURIComponent(input.id)}`, input)
    : api.post<AdminProduct>("/admin/products", input);
}

export function deleteAdminProduct(id: string): Promise<void> {
  return USE_MOCK_API
    ? mock.deleteAdminProduct(id)
    : api.del<void>(`/admin/products/${encodeURIComponent(id)}`);
}

export function getColors(): Promise<AdminColor[]> {
  return USE_MOCK_API ? mock.getColors() : api.get<AdminColor[]>("/admin/color-presets");
}

export function saveColor(input: AdminColor): Promise<AdminColor> {
  if (USE_MOCK_API) return mock.saveColor(input);
  return input.id
    ? api.patch<AdminColor>(`/admin/color-presets/${encodeURIComponent(input.id)}`, input)
    : api.post<AdminColor>("/admin/color-presets", input);
}

export function deleteColor(id: string): Promise<void> {
  return USE_MOCK_API
    ? mock.deleteColor(id)
    : api.del<void>(`/admin/color-presets/${encodeURIComponent(id)}`);
}

/* -------------------------------------------------------------------------- */
/*  Product colour variants + images                                          */
/* -------------------------------------------------------------------------- */

export function getProductColors(productId: string): Promise<ProductColorVariant[]> {
  return USE_MOCK_API
    ? mock.getProductColors(productId)
    : api.get<ProductColorVariant[]>(`/admin/products/${encodeURIComponent(productId)}/colors`);
}

export function createProductColor(input: {
  productId: string;
  nameEn: string;
  nameAr: string;
  hex: string;
  saveAsPreset?: boolean;
}): Promise<ProductColorVariant> {
  return USE_MOCK_API
    ? mock.createProductColor(input)
    : api.post<ProductColorVariant>(
        `/admin/products/${encodeURIComponent(input.productId)}/colors`,
        input,
      );
}

export function updateProductColor(input: {
  id: string;
  nameEn: string;
  nameAr: string;
  hex: string;
}): Promise<ProductColorVariant> {
  return USE_MOCK_API
    ? mock.updateProductColor(input)
    : api.patch<ProductColorVariant>(`/admin/colors/${encodeURIComponent(input.id)}`, input);
}

export function deleteProductColor(id: string): Promise<void> {
  return USE_MOCK_API
    ? mock.deleteProductColor(id)
    : api.del<void>(`/admin/colors/${encodeURIComponent(id)}`);
}

export function reorderProductColor(input: {
  id: string;
  direction: "up" | "down";
}): Promise<void> {
  return USE_MOCK_API
    ? mock.reorderProductColor(input)
    : api.patch<void>(`/admin/colors/${encodeURIComponent(input.id)}/reorder`, {
        direction: input.direction,
      });
}

export function addProductColorImage(input: {
  colorId: string;
  url: string;
}): Promise<ProductColorImage> {
  return USE_MOCK_API
    ? mock.addProductColorImage(input)
    : api.post<ProductColorImage>(`/admin/colors/${encodeURIComponent(input.colorId)}/images`, {
        url: input.url,
      });
}

export function deleteProductColorImage(input: {
  colorId: string;
  imageId: string;
}): Promise<void> {
  return USE_MOCK_API
    ? mock.deleteProductColorImage(input)
    : api.del<void>(
        `/admin/colors/${encodeURIComponent(input.colorId)}/images/${encodeURIComponent(input.imageId)}`,
      );
}

export function reorderProductColorImage(input: {
  colorId: string;
  imageId: string;
  direction: "up" | "down";
}): Promise<void> {
  return USE_MOCK_API
    ? mock.reorderProductColorImage(input)
    : api.patch<void>(`/admin/images/${encodeURIComponent(input.imageId)}/reorder`, {
        colorId: input.colorId,
        direction: input.direction,
      });
}

/** POST /api/uploads — returns the stored file URL. */
export async function uploadBlob(file: File): Promise<string> {
  if (USE_MOCK_API) return mock.uploadBlob(file);
  return (await uploadFile(file)).url;
}

/* -------------------------------------------------------------------------- */
/*  Categories + new arrivals                                                 */
/* -------------------------------------------------------------------------- */

export function getCategories(): Promise<AdminCategory[]> {
  return USE_MOCK_API ? mock.getCategories() : api.get<AdminCategory[]>("/admin/categories");
}

export function saveCategory(input: AdminCategory): Promise<AdminCategory> {
  if (USE_MOCK_API) return mock.saveCategory(input);
  return input.id
    ? api.patch<AdminCategory>(`/admin/categories/${encodeURIComponent(input.id)}`, input)
    : api.post<AdminCategory>("/admin/categories", input);
}

export function deleteCategory(id: string): Promise<void> {
  return USE_MOCK_API
    ? mock.deleteCategory(id)
    : api.del<void>(`/admin/categories/${encodeURIComponent(id)}`);
}

export function getSubcategoryRows(): Promise<AdminCategory[]> {
  return USE_MOCK_API
    ? mock.getSubcategoryRows()
    : api.get<AdminCategory[]>("/admin/subcategories");
}

export function saveSubcategory(input: AdminCategory): Promise<AdminCategory> {
  if (USE_MOCK_API) return mock.saveSubcategory(input);
  return input.id
    ? api.patch<AdminCategory>(`/admin/subcategories/${encodeURIComponent(input.id)}`, input)
    : api.post<AdminCategory>("/admin/subcategories", input);
}

export function deleteSubcategory(id: string): Promise<void> {
  return USE_MOCK_API
    ? mock.deleteSubcategory(id)
    : api.del<void>(`/admin/subcategories/${encodeURIComponent(id)}`);
}

export function getNewArrivalRows(): Promise<AdminProduct[]> {
  return USE_MOCK_API
    ? mock.getNewArrivalRows()
    : api.get<AdminProduct[]>("/admin/new-arrivals");
}

export async function toggleNewArrival(slug: string): Promise<string[]> {
  if (USE_MOCK_API) return mock.toggleNewArrival(slug);
  const result = await api.post<{ slugs: string[] }>(
    `/admin/new-arrivals/${encodeURIComponent(slug)}/toggle`,
  );
  return result.slugs;
}

export async function getNewArrivalSlugs(): Promise<string[]> {
  if (USE_MOCK_API) return mock.getNewArrivalSlugs();
  const result = await api.get<{ slugs: string[] }>("/admin/new-arrivals/slugs");
  return result.slugs;
}

/* -------------------------------------------------------------------------- */
/*  Settings                                                                  */
/* -------------------------------------------------------------------------- */

export function getAppearance(): Promise<AppearanceSettings> {
  return USE_MOCK_API
    ? mock.getAppearance()
    : api.get<AppearanceSettings>("/admin/settings/appearance");
}

export function saveAppearance(input: AppearanceSettings): Promise<AppearanceSettings> {
  return USE_MOCK_API
    ? mock.saveAppearance(input)
    : api.put<AppearanceSettings>("/admin/settings/appearance", input);
}

export function getGeneralSettings(): Promise<GeneralSettings> {
  return USE_MOCK_API
    ? mock.getGeneralSettings()
    : api.get<GeneralSettings>("/admin/settings/general");
}

export function saveGeneralSettings(input: GeneralSettings): Promise<GeneralSettings> {
  return USE_MOCK_API
    ? mock.saveGeneralSettings(input)
    : api.put<GeneralSettings>("/admin/settings/general", input);
}

export function getShippingSettings(): Promise<ShippingSettings> {
  return USE_MOCK_API
    ? mock.getShippingSettings()
    : api.get<ShippingSettings>("/admin/settings/shipping");
}

export function saveShippingSettings(input: ShippingSettings): Promise<ShippingSettings> {
  return USE_MOCK_API
    ? mock.saveShippingSettings(input)
    : api.put<ShippingSettings>("/admin/settings/shipping", input);
}

/**
 * Public zones consumed by checkout and the shipping profile.
 * The backend answers `{ items: [...] }` in snake_case; the storefront reads the
 * camelCase `{ id, name, nameAr, price, days }` shape, so normalize here.
 */
export async function getShippingAreas(): Promise<ShippingArea[]> {
  if (USE_MOCK_API) return mock.getShippingAreas();

  const res = await api.get<{ items?: unknown[] } | unknown[]>("/catalog/shipping-areas");
  const rows = (Array.isArray(res) ? res : res?.items ?? []) as Record<string, unknown>[];

  return rows.map((row) => {
    const daysMin = row.daysMin !== undefined ? Number(row.daysMin) : (row.days_min !== undefined ? Number(row.days_min) : undefined);
    const daysMax = row.daysMax !== undefined ? Number(row.daysMax) : (row.days_max !== undefined ? Number(row.days_max) : undefined);
    return {
      id: String(row.id ?? ""),
      name: String(row.name ?? row.name_en ?? row.nameEn ?? row.name_ar ?? ""),
      price: Number(row.price ?? row.shipping_price ?? row.shippingPrice ?? 0),
      days: Number(row.days ?? daysMax ?? daysMin ?? row.delivery_days ?? row.delivery_time ?? row.deliveryDays ?? 0),
      daysMin,
      daysMax,
      active: (row.active ?? row.is_active ?? true) !== false,
    };
  });

}


/* -------------------------------------------------------------------------- */
/*  Query options                                                             */
/* -------------------------------------------------------------------------- */

export const adminMetricsQuery = () =>
  ({ queryKey: ["admin", "metrics"] as unknown[], queryFn: getMetrics });
export const adminSalesQuery = () =>
  ({ queryKey: ["admin", "sales"] as unknown[], queryFn: getSalesSeries });
export const adminCategorySalesQuery = () =>
  ({ queryKey: ["admin", "category-sales"] as unknown[], queryFn: getCategorySales });
export const adminAnalyticsQuery = (range: AnalyticsRange) =>
  ({
    queryKey: ["admin", "analytics", range.from, range.to] as unknown[],
    queryFn: () => getAnalytics(range),
  });
export const adminOrdersQuery = () =>
  ({ queryKey: ["admin", "orders"] as unknown[], queryFn: getOrders });
export const adminUsersQuery = () =>
  ({ queryKey: ["admin", "users"] as unknown[], queryFn: getUsers });
export const adminProductsQuery = () =>
  ({ queryKey: ["admin", "products"] as unknown[], queryFn: getAdminProducts });
export const adminColorsQuery = () =>
  ({ queryKey: ["admin", "colors"] as unknown[], queryFn: getColors });
export const productColorsQuery = (productId: string) =>
  ({
    queryKey: ["admin", "product-colors", productId] as unknown[],
    queryFn: () => getProductColors(productId),
    enabled: Boolean(productId),
  });
export const adminCategoriesQuery = () =>
  ({ queryKey: ["admin", "categories"] as unknown[], queryFn: getCategories });
export const adminSubcategoriesQuery = () =>
  ({ queryKey: ["admin", "subcategories"] as unknown[], queryFn: getSubcategoryRows });
export const adminNewArrivalsQuery = () =>
  ({ queryKey: ["admin", "new-arrivals"] as unknown[], queryFn: getNewArrivalSlugs });
export const adminAppearanceQuery = () =>
  ({ queryKey: ["admin", "appearance"] as unknown[], queryFn: getAppearance });
export const adminGeneralQuery = () =>
  ({ queryKey: ["admin", "general"] as unknown[], queryFn: getGeneralSettings });
export const adminShippingQuery = () =>
  ({ queryKey: ["admin", "shipping"] as unknown[], queryFn: getShippingSettings });
export const shippingAreasQuery = () =>
  ({ queryKey: ["shipping", "areas"] as unknown[], queryFn: getShippingAreas });

/** Catalogue categories for select inputs, typed for convenience. */
export type { CategoryTab };

